import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMemo } from "react"

import CustomHead from "~/components/SEOHead/CustomHead"
import { getPageSEO } from "~/utils/seo"
import FooterNav from "~/components/LandingPage/FooterNav"
import HeaderNav from "~/components/LandingPage/HeaderNav"
import { HeroToolsSection } from "~/components/LandingPage/HeroToolsSection"
import { VisualEffectsSection } from "~/components/LandingPage/VisualEffectsSection"
import { TopChoiceSection } from "~/components/LandingPage/TopChoiceSection"
import { FeaturedSection } from "~/components/LandingPage/FeaturedSection"
import { NanoBananaGallerySection } from "~/components/LandingPage/NanoBananaGallerySection"
import { FinalCtaSection } from "~/components/LandingPage/FinalCtaSection"
import type { EffectCardData } from "~/components/VideoEffects/EffectCard"
import type { EEffectCategory } from "@aix/vidmax-shared/src/constants/enums"
import { api } from "~/utils/api"
import { AdminGate } from "~/components/AdminGate"
import { NANO_BANANA_GALLERY } from "~/config/nanoBananaGallery"

// Creator-focused landing page. Previously lived at `/`; moved to `/creator` in W17 when
// the UGC ad landing page took over the root route. Kept intact so the creator audience
// still has a home and so pSEO / creator-targeted campaigns have a destination.

const GALLERY_DISPLAY_COUNT = 12

function CreatorLandingPage() {
  const navigate = useNavigate()

  const galleryImages = useMemo(() => {
    const shuffled = [...NANO_BANANA_GALLERY]
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled.slice(0, GALLERY_DISPLAY_COUNT)
  }, [])

  const { data: effectsData, isLoading } = api.presets.listEffects.useQuery({
    limit: 16,
  })

  const effects: EffectCardData[] =
    effectsData?.effects.map((e) => ({
      id: e.id,
      name: e.name,
      shortDescription: e.shortDescription || e.description,
      thumbnailUrl: e.thumbnailUrl,
      previewVideoUrl: e.previewVideoUrl,
      category: e.category as EEffectCategory,
      model: e.model,
      baseCost: e.baseCost,
      badges: e.badges,
    })) || []

  const allEffects = effects.map((e) => ({
    id: e.id,
    name: e.name,
    thumbnailUrl: e.thumbnailUrl,
    previewVideoUrl: e.previewVideoUrl,
    href: `/create/video?preset=${e.id}`,
    badge: e.baseCost ? `${e.baseCost.toFixed(2)}` : undefined,
  }))

  const handleEffectClick = (effectId: string) => {
    void navigate({ to: "/create/video", search: { preset: effectId } })
  }

  const handleImageClick = (imageId: string, prompt?: string) => {
    void navigate({ to: "/create/image", search: { model: "nano-banana-pro", prompt } })
  }

  const homepageSEO = getPageSEO("homepage")

  return (
    <div className="flex flex-col min-h-[100dvh] bg-background">
      <CustomHead
        title={homepageSEO.title}
        description={homepageSEO.description}
        keywords={homepageSEO.keywords}
        pageType={homepageSEO.pageType}
      />
      <HeaderNav />

      <HeroToolsSection />

      <VisualEffectsSection effects={allEffects} isLoading={isLoading} onEffectClick={handleEffectClick} />

      <AdminGate>
        <TopChoiceSection />
      </AdminGate>

      <AdminGate>
        <FeaturedSection />
      </AdminGate>

      <NanoBananaGallerySection images={galleryImages} onImageClick={handleImageClick} />

      <FinalCtaSection />

      <FooterNav />
    </div>
  )
}

export const Route = createFileRoute("/creator")({
  component: CreatorLandingPage,
})
